"use client";

import {
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
} from "react";
import { createPortal } from "react-dom";
import { ChevronDown } from "lucide-react";
import {
  getFontDefinition,
  SUPPORTED_FONTS,
  type PdfFontKey,
} from "@/src/lib/pdf/fonts";
import { classNames } from "../ui/class-names";

export type FontSelectorProps = {
  value: PdfFontKey;
  onChange: (fontFamily: PdfFontKey) => void;
  /** Accessible name; defaults to "Font: {label}". */
  "aria-label"?: string;
  /** White bordered field chrome when nested in FontAndSizeControl. */
  field?: boolean;
  /** Prefix for listbox ids when multiple selectors are on the page. */
  idPrefix?: string;
};

export function FontSelector({
  value,
  onChange,
  "aria-label": ariaLabel,
  field = false,
  idPrefix = "font",
}: Readonly<FontSelectorProps>) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownStyle, setDropdownStyle] = useState<CSSProperties | null>(
    null,
  );
  const currentFont = getFontDefinition(value);
  const listboxId = `${idPrefix}-listbox`;

  const filteredFonts = useMemo(() => {
    const query = search.trim().toLowerCase();

    if (!query) {
      return SUPPORTED_FONTS;
    }

    return SUPPORTED_FONTS.filter((font) =>
      font.label.toLowerCase().includes(query),
    );
  }, [search]);

  function getDropdownStyle(): CSSProperties | null {
    const button = buttonRef.current;

    if (!button) {
      return null;
    }

    const rect = button.getBoundingClientRect();
    const margin = 8;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const minWidth = 12 * 16; // w-48
    const width = Math.max(minWidth, rect.width);
    const right = Math.min(rect.right + margin, viewportWidth - margin);
    const left = Math.max(rect.left, right - width);
    const top = rect.bottom + 4;
    const maxHeight = Math.max(16 * 8, viewportHeight - top - margin);

    return {
      position: "fixed",
      left,
      top,
      width,
      maxHeight,
    };
  }

  function openSelector() {
    setSearch("");
    setActiveIndex(0);
    setDropdownStyle(getDropdownStyle());
    setIsOpen(true);

    requestAnimationFrame(() => {
      inputRef.current?.focus();
    });
  }

  function closeSelector() {
    setIsOpen(false);
    setDropdownStyle(null);
  }

  useLayoutEffect(() => {
    if (!isOpen) {
      return;
    }

    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;

      if (
        dropdownRef.current?.contains(target) ||
        buttonRef.current?.contains(target)
      ) {
        return;
      }

      closeSelector();
    }

    function reposition(event?: Event) {
      const target = event?.target;
      if (
        target instanceof Node &&
        dropdownRef.current &&
        (target === dropdownRef.current || dropdownRef.current.contains(target))
      ) {
        return;
      }

      setDropdownStyle(getDropdownStyle());
    }

    document.addEventListener("mousedown", handleClickOutside);
    // Capture scroll from any ancestor so the portaled list stays on the trigger.
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    window.visualViewport?.addEventListener("resize", reposition);
    window.visualViewport?.addEventListener("scroll", reposition);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
      window.visualViewport?.removeEventListener("resize", reposition);
      window.visualViewport?.removeEventListener("scroll", reposition);
    };
  }, [isOpen]);

  function selectFont(font: (typeof SUPPORTED_FONTS)[number]) {
    onChange(font.key);
    closeSelector();
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((index) => Math.min(index + 1, filteredFonts.length - 1));
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((index) => Math.max(index - 1, 0));
      return;
    }

    if (event.key === "Enter") {
      event.preventDefault();
      const font = filteredFonts[activeIndex];

      if (font) {
        selectFont(font);
      }

      return;
    }

    if (event.key === "Escape") {
      closeSelector();
    }
  }

  const activeFont = filteredFonts[activeIndex];

  return (
    <div
      className={classNames(
        "relative flex min-w-0 items-center",
        field ? "w-full" : "flex-1",
        !field &&
          "rounded-lg border border-zinc-200 bg-zinc-50 p-0.5 transition hover:bg-zinc-100",
      )}
    >
      <button
        ref={buttonRef}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={isOpen ? listboxId : undefined}
        aria-label={ariaLabel ?? `Font: ${currentFont.label}`}
        title={ariaLabel ?? `Font: ${currentFont.label}`}
        onClick={() => (isOpen ? closeSelector() : openSelector())}
        className={classNames(
          "inline-flex min-w-0 flex-1 items-center justify-between gap-1.5 text-xs font-medium text-zinc-950 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand",
          field
            ? "rounded-md border border-zinc-200 bg-white px-2.5 py-2 transition hover:border-zinc-300"
            : "rounded-md px-2.5 py-1",
        )}
      >
        <span className="truncate font-semibold">{currentFont.label}</span>
        <ChevronDown
          aria-hidden="true"
          className={classNames(
            "size-4 shrink-0 text-zinc-500 transition",
            isOpen && "rotate-180",
          )}
        />
      </button>

      {isOpen && dropdownStyle
        ? createPortal(
            <div
              ref={dropdownRef}
              data-document-settings-dropdown=""
              className="z-50 overflow-hidden rounded-md border border-zinc-200 bg-white shadow-lg"
              style={dropdownStyle}
              onMouseDown={(event) => {
                // Keep parent settings popovers from treating this portal as an outside click.
                event.stopPropagation();
              }}
            >
              <div className="border-b border-zinc-200 px-2 py-1.5">
                <input
                  ref={inputRef}
                  type="text"
                  role="combobox"
                  aria-controls={listboxId}
                  aria-expanded={isOpen}
                  aria-autocomplete="list"
                  aria-activedescendant={
                    activeFont
                      ? `${idPrefix}-option-${activeFont.key}`
                      : `${idPrefix}-option-${currentFont.key}`
                  }
                  placeholder="Search fonts..."
                  value={search}
                  onChange={(event) => {
                    setSearch(event.target.value);
                    setActiveIndex(0);
                  }}
                  onKeyDown={handleKeyDown}
                  className="h-9 w-full rounded-md border border-zinc-300 px-2 text-sm outline-none transition focus:border-brand"
                />
              </div>
              <ul
                id={listboxId}
                role="listbox"
                className="max-h-64 overflow-auto py-1"
              >
                {filteredFonts.map((font, index) => (
                  <li
                    key={font.key}
                    id={`${idPrefix}-option-${font.key}`}
                    role="option"
                    aria-selected={font.key === value}
                    onClick={() => selectFont(font)}
                    className={classNames(
                      "cursor-pointer px-3 py-2 text-sm",
                      index === activeIndex
                        ? "bg-zinc-100 text-zinc-950"
                        : "text-zinc-700",
                      font.key === value && "font-semibold",
                    )}
                  >
                    {font.label}
                  </li>
                ))}
                {filteredFonts.length === 0 ? (
                  <li className="px-3 py-2 text-sm text-zinc-500">
                    No fonts found.
                  </li>
                ) : null}
              </ul>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
